import type {
  CartOutcome,
  CartRecoveryKpiValues,
  CartRow,
} from '../../../../types/cartRecovery'
import { T } from '../builder/builderStyles'

const HOUR = 3_600_000
const DAY = 24 * HOUR

const parse = (value?: string | null): Date | null => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const fmtNumber = (n: number): string => n.toLocaleString('en-CA')

export const fmtMoney = (n?: number | null): string =>
  n === null || n === undefined
    ? '—'
    : `$${n.toLocaleString('en-CA', { maximumFractionDigits: 2 })}`

export const fmtPct = (n?: number | null): string => `${n ?? 0}%`

export const fmtDay = (value?: string | null): string => {
  const d = parse(value)
  if (!d) return '—'
  // A date-only value parses as UTC midnight, which is the previous day here.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value ?? '')
  return d.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  })
}

export const fmtDateTime = (value?: string | null): string => {
  const d = parse(value)
  if (!d) return '—'
  const time = d.toLocaleTimeString('en-CA', {
    hour: 'numeric',
    minute: '2-digit',
  })
  return `${fmtDay(value)} · ${time}`
}

export const fmtDuration = (ms?: number | null): string => {
  if (!ms) return '—'
  if (ms < HOUR) return `${Math.round(ms / 60_000)}m`
  if (ms < DAY) return `${Math.round((ms / HOUR) * 10) / 10}h`
  return `${Math.round((ms / DAY) * 10) / 10}d`
}

export const fmtAgo = (value?: string | null, now = Date.now()): string => {
  const d = parse(value)
  if (!d) return ''
  const ms = now - d.getTime()
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / 60_000))}m ago`
  if (ms < DAY) return `${Math.round(ms / HOUR)}h ago`
  const days = Math.round(ms / DAY)
  return days === 1 ? 'Yesterday' : `${days}d ago`
}

export const fmtIn = (value?: string | null, now = Date.now()): string => {
  const d = parse(value)
  if (!d) return ''
  const ms = d.getTime() - now
  if (ms <= 0) return 'now'
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / 60_000))}m`
  if (ms < DAY) return `${Math.round(ms / HOUR)}h`
  return `${Math.round(ms / DAY)}d`
}

export const fmtDelay = (value: number, unit: string): string => {
  if (!value) return 'immediately'
  const label = value === 1 ? unit.replace(/s$/, '') : unit
  return `${value} ${label}`
}

export const OUTCOME_META: Record<
  CartOutcome,
  { label: string; fg: string; bg: string }
> = {
  in_progress: { label: 'In progress', fg: T.blue, bg: T.blueBg },
  queued: { label: 'Queued', fg: T.blue, bg: T.blueBg },
  recovered: { label: 'Recovered', fg: T.green, bg: T.greenLight },
  stopped: { label: 'Stopped', fg: T.amber, bg: T.amberBg },
  lost: { label: 'Lost', fg: T.gray600, bg: T.gray100 },
  skipped: { label: 'Skipped', fg: T.gray600, bg: T.gray100 },
}

// The one line under the reminder dots.
export const reminderCaption = (cart: CartRow): string => {
  const { sent_count: n, total_emails: total, outcome } = cart
  const sent = `${n} of ${total} sent`
  if (outcome === 'in_progress' || outcome === 'queued') {
    if (cart.held) return `${sent} · paused`
    if (!n)
      return cart.next_send_at
        ? `Email 1 in ${fmtIn(cart.next_send_at)}`
        : 'Email 1 soon'
    return cart.next_send_at
      ? `${sent} · next in ${fmtIn(cart.next_send_at)}`
      : sent
  }
  if (outcome === 'stopped') return `${sent} · stopped`
  if (outcome === 'lost') return `${sent} · no order`
  if (outcome === 'recovered') return `${sent} · ordered`
  return sent
}

export interface CartRecoveryKpi {
  key: string
  label: string
  value: string
  sub: string
  // Change against the previous period, when there's one to compare to.
  delta: number | null
  money?: boolean
  tip: string
}

// The seven Cart Recovery numbers, shared by the page and the dashboard widget
// so the two can't drift apart.
export const cartRecoveryKpis = (
  k: CartRecoveryKpiValues,
): CartRecoveryKpi[] => [
  {
    key: 'emailed',
    label: 'Carts emailed',
    value: fmtNumber(k.carts_emailed),
    sub: `of ${fmtNumber(k.abandoned_total)} abandoned`,
    delta: null,
    tip: 'Abandoned carts that got at least one reminder. Carts skipped before Email 1 are counted on the Performance tab.',
  },
  {
    key: 'sent',
    label: 'Reminders sent',
    value: fmtNumber(k.reminders_sent),
    sub: `to ${fmtNumber(k.customers_reached)} customers`,
    delta: null,
    tip: 'Total recovery emails sent. One cart can get several.',
  },
  {
    key: 'open',
    label: 'Open rate',
    value: fmtPct(k.open_rate),
    sub: `${fmtNumber(k.emails_opened)} emails opened`,
    delta: null,
    tip: 'Unique opens divided by reminder emails sent, across all emails.',
  },
  {
    key: 'recovered',
    label: 'Recovered carts',
    value: fmtNumber(k.recovered_carts),
    sub: 'vs previous period',
    delta: k.recovered_carts_delta_pct,
    tip: 'Emailed carts that turned into an order within 7 days. Orders placed before Email 1 are not counted.',
  },
  {
    key: 'rate',
    label: 'Recovery rate',
    value: fmtPct(k.recovery_rate),
    sub: `of ${fmtNumber(k.carts_emailed)}${
      k.in_progress ? ` · ${fmtNumber(k.in_progress)} in progress` : ' emailed'
    }`,
    delta: null,
    tip: 'Recovered carts divided by carts that got at least one reminder. Carts still in progress can still recover, so recent periods look lower until they finish.',
  },
  {
    key: 'revenue',
    label: 'Recovered revenue',
    value: fmtMoney(k.recovered_revenue),
    sub: 'vs previous period',
    delta: k.recovered_revenue_delta_pct,
    money: true,
    tip: 'Order totals from recovered carts, after any discount.',
  },
  {
    key: 'time',
    label: 'Time to recover',
    value: fmtDuration(k.time_to_recover_ms),
    sub: 'median',
    delta: null,
    tip: 'Median time from the cart being abandoned to the order being placed.',
  },
]
